import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, User } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";

interface HeaderProps {
  title?: string;
  breadcrumbs?: { label: string; href?: string }[];
}

// Maps route paths to their display labels and parent paths for breadcrumbs
const routeConfig: Record<string, { label: string; parent?: string }> = {
  "/": { label: "Dashboard" },
  "/employees": { label: "Employees" },
  "/departments": { label: "Departments" },
  "/skills": { label: "Skill Library" },
  "/leaves/my": { label: "My Leaves", parent: "Leaves" },
  "/leaves/custom-types": { label: "Custom Leave Types", parent: "Leaves" },
  "/leaves/approvals": { label: "Team Leave Approvals", parent: "Leaves" },
  "/leaves/calendar": { label: "Leave Calendar", parent: "Leaves" },
  "/leaves/policies": { label: "Leave Policies", parent: "Leaves" },
  "/leaves/holidays": { label: "Public Holidays", parent: "Leaves" },
  "/goals-and-reviews": { label: "Goals & Reviews" },
  "/performance": { label: "Performance" },
  "/performance/reviews": { label: "Performance Reviews", parent: "Performance" },
  "/performance/goals": { label: "Goals", parent: "Performance" },
  "/performance/goals-management": { label: "Goals Management", parent: "Performance" },
  "/performance/my-feedbacks": { label: "My Feedbacks", parent: "Performance" },
  "/performance/knowledge-transfers": { label: "Knowledge Transfers", parent: "Performance" },
  "/payroll": { label: "Payroll" },
  "/payroll/profiles": { label: "Employee Payroll Profiles", parent: "Payroll" },
  "/payroll/salary-structures": { label: "Salary Structures", parent: "Payroll" },
  "/payroll/commission-rules": { label: "Commission Rules", parent: "Payroll" },
  "/payroll/runs": { label: "Payroll Runs", parent: "Payroll" },
  "/payroll/run-new": { label: "New Payroll Run", parent: "Payroll" },
  "/payroll/slips": { label: "Salary Slips", parent: "Payroll" },
  "/payroll/benefits": { label: "Benefits Management", parent: "Payroll" },
  "/payroll/time-attendance": { label: "Time & Attendance", parent: "Payroll" },
  "/onboarding-offboarding": { label: "Onboarding & Offboarding" },
  "/shift-planning": { label: "Shift Planning" },
  "/shift-planning/schedules": { label: "Shift Schedules", parent: "Shift Planning" },
  "/shift-planning/templates": { label: "Shift Templates", parent: "Shift Planning" },
  "/shift-planning/swaps": { label: "Shift Swap Requests", parent: "Shift Planning" },
  "/shift-planning/reports": { label: "Shift Reports & Analytics", parent: "Shift Planning" },
  "/shift-planning/settings": { label: "Settings", parent: "Shift Planning" },
  "/settings": { label: "Settings" },
  "/settings/company": { label: "Company Settings", parent: "Settings" },
  "/settings/organization": { label: "Organization Settings", parent: "Settings" },
  "/settings/users": { label: "User Management", parent: "Settings" },
  "/profile": { label: "Profile" },
};

const Header: React.FC<HeaderProps> = ({ title, breadcrumbs }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  // Get the current route config, handling dynamic segments like /employees/:id
  const getRouteInfo = () => {
    const path = location.pathname;

    // Exact match first
    if (routeConfig[path]) return routeConfig[path];

    // Handle dynamic employee routes like /employees/:id and /employees/:id/edit
    if (/^\/employees\/[^/]+\/edit$/.test(path)) {
      return { label: "Edit Employee", parent: "Employees" };
    }
    if (/^\/employees\/[^/]+$/.test(path)) {
      return { label: "Employee Details", parent: "Employees" };
    }

    return null;
  };

  const routeInfo = getRouteInfo();

  // Determine the page title
  const getPageTitle = () => {
    if (title) return title;
    return routeInfo?.label || "";
  };

  // Build breadcrumb string
  const getBreadcrumb = () => {
    if (!routeInfo) return null;
    if (routeInfo.parent) {
      return `${routeInfo.parent} / ${routeInfo.label}`;
    }
    return routeInfo.label;
  };

  const displayTitle = getPageTitle();
  const breadcrumb = getBreadcrumb();

  const handleSignOut = () => {
    logout();
    navigate("/login");
  };

  const handleProfile = () => {
    navigate("/profile");
    setUserMenuOpen(false);
  };

  const handleSettings = () => {
    navigate("/settings");
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user?.email || "User";
  };

  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || "U";
  };

  return (
    <div className="flex flex-col z-40 transition-colors duration-300">
      {/* Top Row: Title and User Profile */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-8 py-4 flex items-center justify-between min-h-[72px]">
        {displayTitle && (
          <h1 className="text-xl font-bold text-[#1e3a8a] dark:text-white tracking-tight">
            {displayTitle}
          </h1>
        )}

        <div className="flex items-center gap-4">
          {/* User Avatar with colored ring */}
          <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                className="w-9 h-9 rounded-full ring-2 ring-purple-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-900 flex items-center justify-center bg-gradient-to-br from-purple-500 to-indigo-600 cursor-pointer transition-transform hover:scale-105 focus:outline-none"
                title={getUserDisplayName()}
              >
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={getUserDisplayName()}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <span className="text-white font-semibold text-xs">
                    {getUserInitials()}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 p-0 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
              {/* User info section with avatar */}
              <div className="flex items-center gap-3 px-4 py-4">
                <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center bg-slate-200 dark:bg-slate-700">
                  {user?.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={getUserDisplayName()}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-indigo-600 dark:text-indigo-400 font-semibold text-sm">
                      {getUserInitials()}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{getUserDisplayName()}</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{user?.email}</p>
                </div>
              </div>

              <DropdownMenuSeparator className="my-0" />

              {/* Profile Settings */}
              <DropdownMenuItem onClick={handleProfile} className="px-4 py-3 cursor-pointer focus:bg-slate-50 dark:focus:bg-slate-800">
                <User className="mr-3 h-5 w-5 text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Profile Settings</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500">Manage account information</p>
                </div>
              </DropdownMenuItem>

              <DropdownMenuSeparator className="my-0" />

              {/* Sign Out */}
              <DropdownMenuItem
                onClick={handleSignOut}
                className="px-4 py-3 cursor-pointer text-red-500 focus:text-red-500 focus:bg-red-50 dark:focus:bg-red-950/30"
              >
                <LogOut className="mr-3 h-5 w-5" />
                <span className="text-sm font-medium">Sign Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

    </div>
  );
};

export default Header;
